import React, { useContext, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import ResponsiveLayout from './components/layout/ResponsiveLayout'
import { ProtectedRoute } from './components/common/ProtectedRoute'
import GlobalLoader from './components/common/LoadingSpinner'
import { AuthContext } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { RefreshProvider } from './context/RefreshContext'
import { updateMetaTags } from './utils/metaUtils'
import AuthLoadingScreen from './components/common/AuthLoadingScreen'
import { SPLASH_CONFIG } from './config/splashConfig'
import { useSplashScreen } from './hooks/useSplashScreen'
import { useVersionCheck } from './hooks/useVersionCheck'
import GlobalWrappers from './components/layout/GlobalWrappers'

import LoginPage from './pages/auth/LoginPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import SetPasswordPage from './pages/auth/SetPasswordPage'
import AboutPage from './pages/settings/AboutPage'
import HelpAndSupportPage from './pages/settings/HelpAndSupportPage'
import Dashboard from './pages/dashboard/Dashboard'
import FormsPage from './pages/forms/FormsPage'
import FormEntry from './pages/forms/FormEntry'
import FormDataList from './pages/forms/FormDataList'
import Settings from './pages/settings/Settings'
import ThemePage from './pages/settings/ThemePage'
import CompletedForms from './pages/forms/CompletedForms'
import AllForms from './pages/forms/AllForms'
import FormDataView from './pages/forms/FormDataView'
import SyncForms from './pages/sync/SyncForms'
import ProfilePage from './pages/profile/ProfilePage'
import ChangePasswordPage from './pages/profile/ChangePasswordPage'
import ChatPage from './pages/chat/ChatPage'
import ConversationsPage from './pages/chat/ConversationsPage'
import FirebaseChatGuard from './components/chat/FirebaseChatGuard'

const MetaUpdater = () => {
  const location = useLocation()

  useEffect(() => {
    updateMetaTags(location)
    window.scrollTo(0, 0)
  }, [location])

  return null
}

function App() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('App must be used within an AuthProvider')
  }

  const { loading, isAuthenticated, user } = context
  const { showSplash, handleSplashComplete } = useSplashScreen()
  const { 
    showUpdatePrompt, 
    versionInfo, 
    dismissUpdatePrompt, 
    forceRefresh 
  } = useVersionCheck()

  useEffect(() => {
    const setVhUnit = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    setVhUnit()
    
    window.addEventListener('resize', setVhUnit)
    window.addEventListener('orientationchange', () => {
        setTimeout(setVhUnit, 100)
    })

    return () => {
      window.removeEventListener('resize', setVhUnit)
      window.removeEventListener('orientationchange', setVhUnit)
    }
  }, [])

  if (showSplash) {
    return (
      <AuthLoadingScreen
        minDisplayTime={SPLASH_CONFIG.minDisplayTime}
        backgroundColor={SPLASH_CONFIG.backgroundColor}
        onLoadComplete={handleSplashComplete}
      />
    )
  }
  if (loading) return <GlobalLoader />

  return (
    <ThemeProvider>
      <RefreshProvider>
        <GlobalWrappers
          showUpdatePrompt={showUpdatePrompt}
          versionInfo={versionInfo}
          dismissUpdatePrompt={dismissUpdatePrompt}
          forceRefresh={forceRefresh}
        >
          <MetaUpdater />
          <Routes>
          {/* Public Routes */}
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/help" element={<HelpAndSupportPage />} />

          {/* Root Redirect */}
          <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            {user && user.is_first_login === true ? (
              <>
                <Route path="/set-password" element={<SetPasswordPage />} />
                <Route path="*" element={<Navigate to="/set-password" replace />} />
              </>
            ) : (
              <Route element={<ResponsiveLayout />}>
                {/* Dashboard & Main Views */}
                <Route path="/dashboard" element={<Dashboard />} />
                
                {/* Form Management Routes */}
                <Route path="/forms" element={<FormsPage />} />
                <Route path="/forms/completed" element={<CompletedForms />} />
                <Route path="/forms/all" element={<AllForms />} />
                <Route path="/forms/fill/:formId" element={<FormEntry />} />
                <Route path="/forms/edit/:formId/:submissionId" element={<FormEntry />} />
                <Route path="/forms/data" element={<FormDataList />} />
                <Route path="/forms/data/:id" element={<FormDataView />} />
                
                {/* Features & Settings */}
                {/* Chat Features - Guarded by Firebase Auth */}
                <Route element={<FirebaseChatGuard />}>
                  <Route path="/chat" element={<ConversationsPage />} />
                  <Route path="/chat/:id" element={<ChatPage />} />
                </Route>
                <Route path="/sync" element={<SyncForms />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/theme" element={<ThemePage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/change-password" element={<ChangePasswordPage />} />
                <Route path="/set-password" element={<Navigate to="/dashboard" replace />} />
              </Route>
            )}
          </Route>
          <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
        </Routes>
        </GlobalWrappers>
      </RefreshProvider>
    </ThemeProvider>
  )
}

export default App